import React, {useState, useEffect, useRef} from 'react';
import { useLocation } from 'react-router-dom';
import Match from '../components/Match';
import './Calendar.css';

import axios from 'axios';
import { apiLink, domain, URL_WS_URL } from '../variables';
import Pagination from '../components/Calendar/Pagination';
import PlayoffDownEncounter from '../components/Playoffs/PlayOffDownEncounter';
import PageHero from '../components/PageHero';

function getWindowSize() {
    const {innerWidth, innerHeight} = window;
    return {innerWidth, innerHeight};
}

// --- Auto-sélection de la journée courante ---
// Même logique que le composant BoxChamp (box_champ_component) :
// on se positionne sur la journée du match le plus proche d'aujourd'hui,
// parmi les journées >= à la plus haute journée déjà entamée.

const getDayFromMatch = (match) => {
    const libelle = match.libelle.toString();
    return parseInt(libelle.split('°')[1].split(' ')[0]);
}

const getDateFromMatch = (match) => {
    const raw = match.datereelle.toString();
    return new Date(raw.split('/')[2] + '-' + raw.split('/')[1] + '-' + raw.split('/')[0]);
}

const getMaxDayChampionnat = (data) => {
    let max = 1;
    data.forEach((match) => {
        const day = getDayFromMatch(match);
        if (day > max) max = day;
    });
    return max;
}

const getCurrentHighestDay = (data) => {
    const today = new Date();
    let currentHighestDay = 1;
    data.forEach((match) => {
        const date = getDateFromMatch(match);
        const day = getDayFromMatch(match);
        // si le match n'a pas encore eu lieu, il ne compte pas
        if (date > today) return;
        if (day > currentHighestDay) currentHighestDay = day;
    });
    return currentHighestDay;
}

const computeCurrentDay = (data) => {
    if (!data || data.length === 0) return 0;

    const today = new Date();
    const currentHighestDay = getCurrentHighestDay(data);
    const matchParsed = [];

    data.forEach((match) => {
        const date = getDateFromMatch(match);
        const day = getDayFromMatch(match);
        // on ignore les journées déjà passées (antérieures à la plus haute entamée)
        if (day < currentHighestDay) return;
        matchParsed.push({ date, day });
    });

    // tous les matchs ont été joués -> dernière journée
    if (matchParsed.length === 0) return getMaxDayChampionnat(data);

    const nearestMatch = matchParsed.sort((a, b) => {
        const diffA = Math.abs(a.date - today);
        const diffB = Math.abs(b.date - today);
        if (diffA === diffB) return a.day - b.day;
        return diffA - diffB;
    })[0];

    let day = nearestMatch.day;
    const maxDay = getMaxDayChampionnat(data);
    if (day > maxDay) day = maxDay;
    if (day < 1) day = 1;
    return day;
}

function Calendar({championnat}) {
    const matchRef = useRef(null);
    const location = useLocation(); 
    
    const [matchs, setMatchs] = useState(null);
    const [livescoringMatchs, setLivescoringMatchs] = useState([]);

    // Play-offs
    const [playoffMatchs, setPlayoffMatchs] = useState(null);
    const [playdownMatchs, setPlaydownMatchs] = useState(null);

    //
    const [currentDay, setCurrentDay] = useState(0);


    useEffect(() => {
        setCurrentDay(0);
        // on récupère les matchs
        getMatchs();
        getLivescoringMatchs();
        getPlayoffMatchs();
        getPlaydownMatchs();
    }, [championnat]);

    useEffect(() => {
        if (matchs) {
            scrollToMatch()
        }
    }, [matchs]);

    useEffect(() => {
        goToDay();
    }, [playoffMatchs, playdownMatchs])

    const scrollToMatch = () => {
        if (location.state?.goTo) {
            if (matchRef.current) {
                matchRef.current.scrollIntoView({ behavior: "smooth" });
            }
        }
    }

    const goToDay = () => {
        if (location.state?.off_down_matchs_day) {
            const day = parseInt(location.state?.off_down_matchs_day);
            const rencId = parseInt(location.state?.off_down_matchs_id);
            switch (day) {
                case 17:
                case 18:
                    setCurrentDay(12);
                    break;
                case 19:
                case 20:
                    setCurrentDay(13);
                    break;
                case 21:
                case 22:
                    setCurrentDay(14);
                    break;
                default:
                    return;
            }

            if (rencId) {
                if (matchRef.current) {
                    matchRef.current.scrollIntoView({ behavior: "smooth" });
                }
            }
        }
    }

    // on récupère les matchs de playoffs
    const getPlayoffMatchs = async () => {
        const url = `${domain}/server/getPODMatchs.php`;
        try {
            const response = await axios.post(url, {
                division: championnat,
                day: 0,
                offOrDown: "off",
                withSets: false
            })

            if (response) {
                setPlayoffMatchs(response.data);
            }
        } 
        catch (error) {
            console.log(error);
        }
    }

    // on récupère les matchs de playdowns
    const getPlaydownMatchs = async () => {
        const url = `${domain}/server/getPODMatchs.php`;
        try {
            const response = await axios.post(url, {
                division: championnat,
                day: 0,
                offOrDown: "down",
                withSets: false
            })

            if (response) {
                setPlaydownMatchs(response.data);
            }
        } 
        catch (error) {
            console.log(error);
        }
    }

    const getMatchs = async () => {
        const link = apiLink.find(item => item.for === championnat).link;
        if (link !== '') {
            axios
            .get(link, {
                "Content-Type": "application/xml; charset=utf-8"
            })
            .then(function(res) {
                var data = res.data;
                setMatchs(data);
                // auto-sélection de la journée courante (J1, J2, ...),
                // sauf si on arrive via une redirection explicite (match précis / playoffs)
                if (!location.state?.goTo && !location.state?.off_down_matchs_day) {
                    setCurrentDay(computeCurrentDay(data));
                }
            })
            .catch(function(error) {
                console.log(error);
            });
        } 
    }

    const getLivescoringMatchs = async () => {
        const link = URL_WS_URL + "/livescore/all_livescores";
        axios
        .get(link, {
            "Content-Type": "application/xml; charset=utf-8"
        })
        .then(function(res) {
            var data = res.data;
            setLivescoringMatchs(data)
        })
        .catch(function(error) {
            console.log(error);
        });
    }

    const getHeaderLivescore = (renc_id) => {
        let headerLivescore = {};
        if (livescoringMatchs !== null && livescoringMatchs !== undefined) {
            if (livescoringMatchs.list !== undefined) {
                for (const div of livescoringMatchs.list) {
                    // S'il y a des matchs dans la div alors on regarde s'il y a le match qu'on veut
                    const match = div.matchs.find(item => item.renc_id === renc_id.toString());
                    if (match !== undefined) {
                        headerLivescore = match;
                        break; // Sort complètement de la boucle externe
                    }
                }
            }
        }
        return headerLivescore;
    }
    const getTotalDayPerChampionnat = () => {
        let total = 0;
        if (matchs !== null) {
            matchs.forEach((match) => {
                let tour = match.libelle.toString();
                tour = parseInt(tour.split('°')[1].split(' ')[0]);
                if (tour > total) {
                    total = tour;
                }
            })
        }
        // on veut ajouter les playoffs
        if (championnat === 'probm' || championnat === "proad" || championnat === 'proam') {
            total += 3;
        }
        return total;
    }


    const handleDayClick = (e, value) => {
        if (value === currentDay) { 
            setCurrentDay(0) // if already clicked then we cancel the filter
        }
        else {
            setCurrentDay(value) // otherwise we go to day x
        }
    }


    const getDayMatch = (index) => {
        let tour = matchs[index].libelle.toString();
        tour = tour.split('°')[1].split(' ')[0];
        return parseInt(tour);
    }

    const getRencID = (index) => {
        const cData = matchs[index].lien;
        const rencID = cData.split('renc_id=')[1].split('&')[0];
        return rencID;
    }

    // Fonction pour la concordance avec les journées SPID
    const renderMatchPlayOffDown = (day, matchDay) => {
        day = parseInt(day);
        matchDay = parseInt(matchDay);

        if (championnat === 'proam') {
            // Demi-Finale
            if ((matchDay === 23 || matchDay === 24) && day === 19) {
                return true;
            }
            // Finale
            return (matchDay === 25 && day === 20);
        } else {
            // matchDay représente la journée SPID
            // day représente le numéro de notre page sur le site / le numéro de la journée
            // Il y a deux matchs par "journée"
            // Quart de finale
            if ((matchDay === 16 || matchDay === 17) && day === 12) {
                return true;
            }
            // Demi-Finale
            if ((matchDay === 18 || matchDay === 19) && day === 13) {
                return true;
            }
            // Finale
            return (matchDay === 20 || matchDay === 21) && day === 14;
        }
    }

    // Titre de la journée affichée : "Journée n", ou la phase finale correspondante
    const getPhaseLabel = (day) => {
        if (championnat === 'proam') {
            if (day === 19) return 'Demi-finales';
            if (day === 20) return 'Finale';
        }
        else if (championnat === 'probm' || championnat === 'proad') {
            if (day === 12) return 'Quarts de finale';
            if (day === 13) return 'Demi-finales';
            if (day === 14) return 'Finale';
        }
        return 'Journée ' + day;
    }

    const formatDateCourte = (value) => {
        const [d, m, y] = (value || '').split('/');
        const date = new Date(y + '-' + m + '-' + d);
        return isNaN(date.getTime()) ? value : date.toLocaleDateString('fr-FR', {day: 'numeric', month: 'short'});
    }

    const dayMatchs = matchs !== null
        ? matchs.map((item, index) => ({item, index})).filter(({index}) => currentDay === 0 || getDayMatch(index) === currentDay)
        : [];
    const countPlayOffDown = [playoffMatchs, playdownMatchs]
        .map(list => Array.isArray(list) ? list.filter(match => renderMatchPlayOffDown(currentDay, match?.day_id)).length : 0)
        .reduce((a, b) => a + b, 0);
    const dayDates = [...new Set(dayMatchs.map(({item}) => item.datereelle).filter(Boolean))];
    const dayDatesLabel = dayDates.length === 0 ? '' :
        dayDates.length <= 2 ? dayDates.map(formatDateCourte).join(' / ') :
        formatDateCourte(dayDates[0]) + ' – ' + formatDateCourte(dayDates[dayDates.length - 1]);

    return (
        <div className={"calendar_page " + championnat}>

            {/* // BANNIERE */}
            <PageHero championnat={championnat} title="Calendrier / Résultats" />

            <div className="calendar_container calendar_content">
                <Pagination
                    division={championnat}
                    currentPage={currentDay}
                    totalPage={getTotalDayPerChampionnat()}
                    handleDayClick={handleDayClick}
                    visible={true}
                />

                {
                    matchs !== null &&
                    <div className="calendar_day_header">
                        <h2 className="calendar_day_title">
                            {currentDay === 0 ? 'Toutes les rencontres' : getPhaseLabel(currentDay)}
                            <span className="calendar_day_count">{dayMatchs.length + countPlayOffDown}</span>
                        </h2>
                        {
                            currentDay !== 0 && dayDatesLabel &&
                            <span className="calendar_day_dates">{dayDatesLabel}</span>
                        }
                    </div>
                }

                {
                    matchs !== null && dayMatchs.length + countPlayOffDown === 0 &&
                    <p className="calendar_empty">Aucune rencontre programmée pour le moment.</p>
                }

                <div className="matchs_container match_cards">
                    {
                        dayMatchs.map(({item, index}) => {
                            return (
                                <div key={getRencID(index)} id={getRencID(index)} ref={parseInt(getRencID(index)) === parseInt(location.state?.goTo) ? matchRef : null}>
                                    <Match
                                        championnat={championnat}
                                        match={item}
                                        deploy={location.state?.goTo}
                                        showTour={currentDay === 0}
                                        headerLivescore={getHeaderLivescore(getRencID(index))}
                                        currentDay={getDayMatch(index)}
                                        variant="cards"
                                    />
                                </div>
                            )
                        })
                    }
                    {playoffMatchs?.map((match, index) => {
                        return (
                            (renderMatchPlayOffDown(currentDay, match?.day_id)) && (
                                <div 
                                    key={match?.renc_id} 
                                    id={match?.renc_id}
                                    ref={parseInt(match?.renc_id) === parseInt(location.state?.off_down_matchs_id) ? matchRef : null}
                                >
                                    <PlayoffDownEncounter
                                        division={championnat}
                                        match={match} 
                                        headerLivescore={getHeaderLivescore(match?.renc_id)}
                                        deploy={parseInt(match?.renc_id) === parseInt(location.state?.off_down_matchs_id)}
                                        offOrDown="off"
                                    />
                                </div>
                            )
                        )
                    })}
                    {playdownMatchs?.map((match, index) => {
                        return (
                            (renderMatchPlayOffDown(currentDay, match?.day_id)) && (
                                <div 
                                    key={match?.renc_id} 
                                    id={match?.renc_id}
                                    ref={parseInt(match?.renc_id) === parseInt(location.state?.off_down_matchs_id) ? matchRef : null}
                                >
                                    <PlayoffDownEncounter
                                        division={championnat}
                                        match={match} 
                                        headerLivescore={getHeaderLivescore(match?.renc_id)}
                                        deploy={parseInt(match?.renc_id) === parseInt(location.state?.off_down_matchs_id)}
                                        offOrDown="down"
                                    />
                                </div>
                            )
                        )
                    })}
                </div>
                <Pagination
                    division={championnat}
                    currentPage={currentDay} 
                    totalPage={getTotalDayPerChampionnat()}
                    handleDayClick={handleDayClick} 
                    visible={getWindowSize().innerWidth <= 1000}
                />
            </div>

            


        </div>
    );
}

export default Calendar;